import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { AlertNotificationModel } from '../models/alert.model'
import { AutoAlertKeywordModel, RuleModel } from '../models/automations.model'
import {
    ChannelContactModel,
    ChannelConversationStatus,
    Contact, ContactType, ConversationModel, DeletedGroupContactData, Group, GroupChatModel, OutLookResponse,
    pushChannelHistory,
    pushConversation,
    pushConversationInContact,
    pushHistory,
    sortContacts,
    sortConversations,
    TypeAction,
    TypingDataModel, updateContactsInConversations, updateContactsInGroup, updateContactTypesInConversations, updateGroupsInContacts
} from '../models/conversations'
import { FeaturesModel } from '../models/features.model'
import { KafkaModel } from '../models/kafka.model'
import { OrganizationModel } from '../models/organization.model'
import { PhoneNumberModel, ProviderModel } from '../models/phone-number.model'
import { mapToPlanMetrics, PlanMetrics } from '../models/plan-metrics.model'
import { PlanModel } from '../models/plans.model'
import { ProfileModel } from '../models/profile.model'
import { ReportsModel } from '../models/reports.model'
import { RoleModel } from '../models/roles.model'
import { Setting, SettingsModel } from '../models/settings-model'
import { TemplateModel } from '../models/templates.models'
import { PlanHistoryModel } from '../models/transaction-history.model'
import { ChannelPlanStatus, ChannelUserModel, UserModel } from '../models/users.model'
import { SettingsService } from '../services/SettingsService'
import { uniqBy } from 'lodash'
import { AppState } from './models'
import { UserService } from '../services/UserService'
import { active } from 'd3'
import { ChannelOrganizationModel } from '../models/channels/channels.model'

const initialState: AppState = {
    userRoleId: UserService.getLoggedInUserRole() ?? 0,
    Conversations: [],
    Organizations: [],
    OrganizationDetails: [],
    History: {},
    ChannelHistory: {},
    ContactTypes: [],
    Contacts: [],
    AllContacts: [],
    Users: [],
    AllUsers: [],
    Groups: [],
    Features: [],
    Settings: {
        BlockedContacts: [],
        UnSubscribedContacts: [],
        AllowedWrongPasswordAttempts: 0,
        NotificationsEnabled: false,
        TwoFactAuthEnabled: false,
        Settings: [],
        VoiceVerificationEnabled: true,
    } as SettingsModel,
    Profile: {
        FullName: 'U K',
        Email: '',
        FirstName: '',
        ImageReferenceId: '',
        LastName: '',
        PhoneNumber: '',
        OrganizationName: '',
        OrgId: JSON.parse(localStorage.getItem("current-user")?.toString() || "{}")
            ?.organizationId || '',
        RoleID: '',
    } as ProfileModel,
    IsMobileDevice: window.outerWidth <= 835,
    ShowSideMenu: false,
    IsTeamsApp: window.location.pathname.includes('teams'),
    DeviceWidth: window.outerWidth,
    DeviceHeight: window.innerHeight,
    PhoneNumbers: [],
    Roles: [],
    Plans: [],
    Providers: [],
    AlertNotifications: [],
    UserAlertNotifications: [],
    RulesKeywords: [],
    Templates: [],
    AllKeywords: [],
    PrivateRules: [],
    AllPrivateRules: [],
    GlobalRules: [],
    AllGlobalRules: [],
    Reports: [],
    PlanHistory: [],
    GetBlockedContacts: [],
    GetUnSubscribedContacts: [],
    GroupContactsDeletionData: [],
    TypingData: [],
    SecureFiles: [],
    SecureFileLinks: [],
    ChannelTemplates: [],
    FacebookTemplates: [],
    ChannelUsers: [],
    ActiveChannelsTypes: [],
    ChannelAllUsers: [],
    ChannelAllOrganizations: [],
    ChannelContacts: [],
    IsWordPress: false,
    WordPressDomain: "",

    Shopify: false,
    Shopify_Shop: "",
    Shopify_Access_Token: "",

    Tutorials: {},
    OnboardingDetails: {
        OnboardingStatus: false,
        CheckPoints: []
    },
    PlanDetails: {
        PlanId: "",
        PLanName: "",
        Status: "" as ChannelPlanStatus,
        Price: "",
        Features: [],
        FeatureKeys: [],
        Quantity: 0,
        AddOns: [],
        IsReccuring: false,
        IsMonthly: false,
        IsYearly: false,
        Created_At: "",
        Last_Updated_At: "",
        Start_Date: "",
        End_Date: ""
    },

    LoadingConversations: false,
    LoadingHistory: false,
    LoadingContacts: false,
    LoadingContactTypes: false,
    LoadingUsers: false,
    LoadingGroups: false,
    LoadingOrganizations: false,
    LoadingSettings: false,
    LoadingProfile: false,
    LoadingFeatures: false,
    LoadingRoles: false,
    LoadingPhoneNumbers: false,
    LoadingPlans: false,
    LoadingProviders: false,
    LoadingOrganizationDetails: false,
    LoadingAlertsNotications: false,
    LoadingAutoAlertKeywords: false,
    LoadingTemplates: false,
    LoadedConversations: false,
    LoadedHistory: false,
    LoadedContacts: false,
    LoadedContactTypes: false,
    LoadedUsers: false,
    LoadedGroups: false,
    LoadedOrganizations: false,
    LoadedSettings: false,
    LoadedProfile: false,
    LoadedFeatures: false,
    LoadedRoles: false,
    LoadedPhoneNumbers: false,
    LoadedPlans: false,
    LoadedProviders: false,
    LoadedOrganizationDetails: false,
    LoadingAutomationPrivateRules: false,
    LoadingAutomationGlobalRules: false,
    Channels: [],
    LoadingReports: false,
    LoadingKafka: false,
    Kafka: [],
    LoadingSecureFiles: false,
    LoadingSecureFileLinks: false,
    ChannelConversations: [],
    LoadingConnect: false,
    LoadingChannels: false,
    IsMassContactsDeletionInProgress: false,
    IsMassGroupsDeletionInProgress: false,
    HandleMassContactsActionEvent: false,
    HandleMassGroupsActionEvent: false,
    SyncingContacts: {
        flag: false,
        existingContacts: 0,
        ignoredContacts: 0,
        savedContacts: 0,
        totalContacts: 0,
        error: "",
        loadContacts: false
    },
    NewMessageRecived: [],
    PlanMetrics: mapToPlanMetrics(
        JSON.parse(localStorage.getItem("current-user")?.toString() || "{}").planTracker
    ),
    ShowingAlertsCount: 0,
    Toasts: [],
    ExportMessageLogs: [],
    IsMessageLogDownloadReportMsg: '',
    WebNotificationText: {},
    OrganizationName: "",
    TrainChatHistoryStatus: "",
    AiTrainedFiles: [],
    AiTrainedUrls: [],
    AiFeaturesOrgLevel: false,
    AiCreditsCount: 0,
    channelPlans: []
}

const counterSlice = createSlice({
    name: 'appRedeucer',
    initialState,
    reducers: {
        updateUserRoleId: (state: AppState, action: PayloadAction<number>) => {
            state.userRoleId = action.payload
        },

        updateConversations: (state: AppState, action: PayloadAction<ConversationModel[]>) => {
            state.Conversations = updateContactTypesInConversations(
                updateContactsInConversations(action.payload, state.Contacts),
                state.ContactTypes
            );
            state.LoadingConversations = false;
            state.LoadedConversations = true;
        },

        updateLoadingConversations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingConversations = action.payload;
        },

        updateHistory: (state: AppState, action: PayloadAction<{ phoneNumber: string, history: ConversationModel[] }>) => {
            state.History = {
                ...state.History,
                [action.payload.phoneNumber]: action.payload.history
            }
        },

        clearHistory: (state: AppState, action: PayloadAction<{ [key: string]: ConversationModel[] }>) => {
            state.History = {}
        },

        cleanHistory: (state: AppState, action) => {
            let history = state.History[action.payload?.replace('+', '')]
            history = history?.filter((x) => x.ID) || []
            let oldCon = state.Conversations.find(
                (x) => x.PhoneNumber === action.payload
            )
            let newCon = history[history?.length - 1]
            if (oldCon) {
                newCon.Contact = oldCon.Contact
            }
            let conv =
                history?.length >= 1
                    ? [
                        ...state.Conversations?.filter(
                            (x) => x.PhoneNumber !== action.payload
                        ),
                        newCon,
                    ]
                    : []
            state.Conversations = sortConversations(conv);
            state.History = {
                ...state.History,
                [action.payload?.replace('+', '')]: history,
            };
        },

        updateContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            let contactData = action.payload.filter((x: Contact, index: number) => action.payload.findIndex((y: Contact) => y.ID === x.ID) === index).filter((x: Contact) => !(x.IsDeleted || !x.FirstName));
            state.AllContacts = action.payload;
            state.Contacts = sortContacts(updateGroupsInContacts(contactData, state.Groups));
            state.Groups = updateContactsInGroup(state.Groups, action.payload);
            state.Settings.BlockedContacts = action.payload?.filter((x: Contact) => x.IsBlocked);
            state.Settings.UnSubscribedContacts = action.payload?.filter(
                (x: Contact) => x.IsUnsubscribed
            );
            state.LoadingContacts = false;
            state.LoadedContacts = true;
            state.IsMassContactsDeletionInProgress = false;
        },

        updateLoadingContacts: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingContacts = action.payload
        },

        updateContactTypes: (state: AppState, action: PayloadAction<ContactType[]>) => {
            state.ContactTypes = action.payload
        },

        updateContactsInConversation: (state: AppState, action) => {
            let contactsData = action.payload.filter((x: Contact, index: number) => action.payload.findIndex((y: Contact) => y.ID === x.ID) === index).filter((x: Contact) => !(x.IsDeleted || !x.FirstName));
            state.Conversations = updateContactsInConversations(
                state.Conversations,
                contactsData
            );
        },

        updateContactTypesInConversation: (state: AppState, action) => {
            state.Conversations = updateContactTypesInConversations(
                state.Conversations,
                action.payload
            );
        },
        pushConversations: (state: AppState, action: PayloadAction<ConversationModel[]>) => {
            state.Conversations = pushConversation(
                state.Conversations,
                state.Contacts,
                action.payload
            );
            state.Contacts = pushConversationInContact(state.Contacts, action.payload);
            state.History = pushHistory(state.History, action.payload);
            state.NewMessageRecived = action.payload.filter(x => !x.IsOutbound)
        },

        updateNewMessageRecived: (state, action) => {
            state.NewMessageRecived = action.payload
        },

        pushContacts: (state: AppState, action) => {
            let { Conversations, Contacts } = state;
            console.log(action.payload)
            action.payload?.forEach((contact: Contact) => {
                let _conversation = Conversations?.find(x => x.PhoneNumber === contact.PhoneNumber || x.Contact?.ID === contact.ID);
                let _contact = Contacts?.find(x => x.ID === contact.ID);
                if (_conversation) {
                    _conversation.UnReadCount = contact.UnReadCount;
                    _conversation.IsBlocked = contact.IsBlocked;
                }

                if (_conversation?.Contact?.ID === contact.ID && contact.IsDeleted) {
                    _conversation.Contact = undefined;
                }

                if (_contact) {
                    if (_contact.IsBlocked !== contact.IsBlocked) {
                        _contact.IsBlocked = contact.IsBlocked;
                        _contact.CreatedDateTime = contact.CreatedDateTime;
                    }
                    _contact.UnReadCount = contact.UnReadCount;
                    _contact.IsDeleted = contact.IsDeleted;
                }
            });
            if (!!state.Contacts?.filter((x) => action.payload.map(y => y.ID)?.includes(x.ID))) {
                state.Contacts = sortContacts([
                    ...state.Contacts?.filter((x) => !action.payload.map(y => y.ID)?.includes(x.ID)),
                    ...action.payload,
                ]);
            }
            else {
                state.Contacts = sortContacts([...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName)))
            }

            // const contacts = [...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName))
            state.Conversations = [...Conversations];
            // state.Contacts = sortContacts([...contacts,]);
            state.Groups = updateContactsInGroup(state.Groups, Contacts);
            state.Settings = {
                ...state.Settings,
                BlockedContacts: Contacts?.filter((x: Contact) => x.IsBlocked),
                UnSubscribedContacts: Contacts?.filter((x: Contact) => x.IsUnsubscribed)
            }
        },

        pushGroupChat: (state: AppState, action: PayloadAction<GroupChatModel[]>) => {
            let { Conversations } = state;
            action.payload?.forEach((x: GroupChatModel) => {
                let con = Conversations?.find(y => y.GroupChat?.ID === x.ID)
                if (con) {
                    con.GroupChat.UnReadCount = x.UnReadCount;
                }
            });
            state.Conversations = [...Conversations];
        },

        updateGroups: (state: AppState, action) => {
            state.Groups = updateContactsInGroup(action.payload, state.Contacts);
            state.Contacts = updateGroupsInContacts(state.Contacts, action.payload);
            state.LoadingGroups = false;
            state.LoadedGroups = true;
            state.IsMassGroupsDeletionInProgress = false;
        },

        updateLoadingGroups: (state: AppState, action) => {
            state.LoadingGroups = action.payload
        },

        pushGroups: (state: AppState, action: PayloadAction<Group[]>) => {
            let groups = [...state.Groups?.filter((x) => !action.payload?.map((y: Group) => y.ID)?.includes(x.ID)), ...action.payload]?.filter((x: Group) => !x.IsDeleted)
            state.Groups = updateContactsInGroup(groups, state.Contacts);
            // state.Contacts = updateGroupsInContacts(state.Contacts, groups);
        },

        updateUnsubscribedContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.GetUnSubscribedContacts = action.payload;
        },

        updateBlockedContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.GetBlockedContacts = action.payload;
        },

        // updateProfile: (state: AppState, action: PayloadAction<ProfileModel>) => {
        //     state.Profile = { ...action.payload, OrganizationName: state.Profile?.OrganizationName };
        //     state.LoadingProfile = false;
        //     state.LoadedProfile = true;
        // },

        updatePlanMetrics: (state: AppState, action: PayloadAction<PlanMetrics>) => {
            state.PlanMetrics = { ...action.payload } as PlanMetrics;
        },

        // updateLoadingProfile: (state: AppState, action: PayloadAction<boolean>) => {
        //     state.LoadingProfile = action.payload;
        // },

        updateOrgName: (state: AppState, action: PayloadAction<ProfileModel>) => {
            // state.Profile = { ...state.Profile, OrganizationName: action.payload as any} as ProfileModel;
            state.OrganizationName = action.payload as any;
        },

        updateContact: (state: AppState, action) => {
            // state.Contacts = sortContacts([
            //     ...state.Contacts?.filter((x) => x.ID !== action.payload.ID),
            //     action.payload,
            // ]);
            state.Groups = updateContactsInGroup(state.Groups, [
                ...state.Contacts?.filter((x) => x.ID !== action.payload.ID),
                action.payload,
            ]);
            state.Conversations = sortConversations(
                [
                    ...state.Conversations?.filter(
                        (x) => x.PhoneNumber !== action.payload?.PhoneNumber
                    ),
                    {
                        ...state.Conversations?.find(
                            (x) => x.PhoneNumber === action.payload?.PhoneNumber
                        ),
                        Contact: { ...action.payload },
                    } as ConversationModel,
                ]?.filter((x) => x.PhoneNumber)
            )
        },

        updateConversation: (state: AppState, action: PayloadAction<ConversationModel>) => {
            state.Conversations = sortConversations([
                ...state.Conversations?.filter(
                    (x) => x.GroupChat?.ID ? x.GroupChat.ID !== action.payload?.PhoneNumber : x.PhoneNumber !== action.payload?.PhoneNumber
                ),
                { ...action.payload } as ConversationModel
            ])
        },

        updateSettings: (state: AppState, action) => {
            state.Settings = {
                ...state.Settings,
                Settings: action.payload,
                AllowedWrongPasswordAttempts: parseInt(
                    action.payload?.find(
                        (x: Setting) =>
                            x.Code === SettingsService.Wrong_Password_Attmps_Code
                    )?.Value || state.Settings?.AllowedWrongPasswordAttempts
                ),
                NotificationsEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Notification_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Notification_Code
                        )
                        .Value?.toLowerCase() === 'true'
                    : state.Settings.NotificationsEnabled,
                TwoFactAuthEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Two_FactAuth_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Two_FactAuth_Code
                        )
                        ?.Value.toLowerCase() === 'true'
                    : state.Settings.TwoFactAuthEnabled,
                VoiceVerificationEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Voice_Otp_Veification
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) =>
                                x.Code === SettingsService.Voice_Otp_Veification
                        )
                        .Value?.toLowerCase() === 'true'
                    : state.Settings?.VoiceVerificationEnabled,
            }

            let setTimeZone = action.payload?.find(x => x.Code === SettingsService.Auto_Time_Zone_Code)?.Value?.toString()?.toLowerCase() === "false" ? action.payload?.find(x => x.Code === SettingsService.Time_Zone_Code)?.Value?.toString() : ""
            localStorage.setItem("Timezone", setTimeZone)
        },

        updateMenuFlag: (state: AppState, action: PayloadAction<boolean>) => {
            state.ShowSideMenu = action.payload
        },

        updateMobileDevice: (state: AppState) => {
            let width = window.outerWidth < 320 ? window.innerWidth : window.outerWidth
            state.IsMobileDevice = width <= 835 && width !== 0;
            state.DeviceWidth =
                width < 320
                    ? state.DeviceWidth < 320
                        ? 320
                        : state.DeviceWidth
                    : width;
            state.DeviceHeight = window.innerHeight
        },

        updateFeatures: (state: AppState, action: PayloadAction<FeaturesModel[]>) => {
            state.Features = action.payload;
            state.LoadingFeatures = false;
            state.LoadedFeatures = true;
        },

        updateLoadingFeatures: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingFeatures = action.payload
        },

        updateOrganizations: (state: AppState, action: PayloadAction<OrganizationModel[]>) => {
            state.Organizations = action.payload;
            state.LoadingOrganizations = false;
            state.LoadedOrganizations = true;
        },

        updateLoadingOrganizations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingOrganizations = action.payload
        },

        updatePhoneNumber: (state: AppState, action: PayloadAction<PhoneNumberModel>) => {
            state.PhoneNumbers = [
                action.payload,
                ...state.PhoneNumbers?.filter((x) => x.ID !== action.payload.ID),
            ];
            state.LoadingPhoneNumbers = false;
            state.LoadedPhoneNumbers = true;
        },

        updatePhoenNumbers: (state: AppState, action: PayloadAction<PhoneNumberModel[]>) => {
            state.PhoneNumbers = action.payload;
            state.LoadingPhoneNumbers = false;
            state.LoadedPhoneNumbers = true;
        },

        updateLoadingPhoneNumbers: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingPhoneNumbers = action.payload;
        },

        updateRoles: (state: AppState, action: PayloadAction<RoleModel[]>) => {
            state.Roles = action.payload;
        },

        updatePlans: (state: AppState, action: PayloadAction<PlanModel[]>) => {
            state.Plans = action.payload;
            state.LoadingPlans = false;
            state.LoadedPlans = true;
        },

        updateLoadingPlanes: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingPlans = action.payload
        },

        updateProviders: (state: AppState, action: PayloadAction<ProviderModel[]>) => {
            state.Providers = action.payload;
            state.LoadingProviders = false;
            state.LoadedProviders = true;
        },
        updateLoadingProviders: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingProviders = action.payload
        },

        headerTitle: (state: AppState, action) => {
            state.HeaderTitle = action.payload;
            state.HeaderCount = undefined;
            state.RefreshHandler = undefined;
            state.BackHandler = undefined
        },

        headerCount: (state: AppState, action: PayloadAction<string>) => {
            state.HeaderCount = action.payload
        },

        refreshHandler: (state: AppState, action) => {
            state.RefreshHandler = action.payload
        },

        backHandler: (state: AppState, action) => {
            state.BackHandler = action.payload;
        },

        updateAlertNotification: (state: AppState, action: PayloadAction<AlertNotificationModel[]>) => {
            state.AlertNotifications = action.payload;
            state.LoadingAlertsNotications = false;
        },

        updateLoadingAlertNotification: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAlertsNotications = action.payload
        },

        updateUserAlertNotifaction: (state: AppState, action: PayloadAction<AlertNotificationModel[]>) => {
            state.UserAlertNotifications = action.payload
        },

        setShowingAlert: (state: AppState, action: PayloadAction<number>) => {
            state.ShowingAlertsCount = action.payload
        },

        updateLoadingKeywords: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutoAlertKeywords = action.payload;
        },

        updateKeywords: (state: AppState, action: PayloadAction<AutoAlertKeywordModel[]>) => {
            state.RulesKeywords = action.payload?.filter(
                (x: AutoAlertKeywordModel) => !x.IsDeleted
            );
            state.AllKeywords = action.payload;
            state.LoadingAutoAlertKeywords = false;
        },

        updateTemplates: (state: AppState, action: PayloadAction<TemplateModel[]>) => {
            state.Templates = action.payload;
            state.LoadingTemplates = false;
        },

        updateLoadingTemplates: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingTemplates = action.payload;
        },

        updateUsers: (state: AppState, action: PayloadAction<UserModel[]>) => {
            let re =
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            state.Users = action.payload?.filter((x: UserModel) =>
                re.test(String(x.Email).toLowerCase())
            );
            state.AllUsers = action.payload;
            state.LoadingUsers = false;
            state.LoadedUsers = true;
        },

        updateLoadingUsers: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingUsers = false
        },

        updateLoadingPrivateAutomationRules: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutomationPrivateRules = action.payload
        },

        updateLoadingGlobalAutomationRules: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutomationGlobalRules = action.payload;
        },

        updatePrivateAutomationRules: (state: AppState, action: PayloadAction<RuleModel[]>) => {
            state.PrivateRules = action.payload?.filter((x: RuleModel) => !x.IsDeleted);
            state.AllPrivateRules = action.payload;
            state.LoadingAutomationPrivateRules = false;
        },

        updateGlobalAutomationRules: (state: AppState, action: PayloadAction<RuleModel[]>) => {
            state.GlobalRules = action.payload?.filter((x: RuleModel) => !x.IsDeleted);
            state.AllGlobalRules = action.payload;
            state.LoadingAutomationGlobalRules = false;
        },

        updateReports: (state: AppState, action: PayloadAction<ReportsModel[]>) => {
            state.Reports = action.payload;
            state.LoadingReports = false;
        },

        loadingReports: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingReports = action.payload
        },

        updatePlanHistory: (state: AppState, action: PayloadAction<PlanHistoryModel[]>) => {
            state.PlanHistory = action.payload
        },

        setKafka: (state: AppState, action: PayloadAction<KafkaModel[]>) => {
            state.Kafka = action.payload?.sort((a: KafkaModel, b: KafkaModel) => a.GroupId > b.GroupId ? 1 : -1)
        },

        updateKafka: (state: AppState, action: PayloadAction<KafkaModel>) => {
            state.Kafka = [...state.Kafka?.filter(x => x.GroupId !== action.payload?.GroupId), action.payload]
                .sort((a: KafkaModel, b: KafkaModel) => a.GroupId > b.GroupId ? 1 : -1)
        },

        loadingKafkaOrg: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingKafka = action.payload
        },

        syncingContacts: (state: AppState, action: PayloadAction<OutLookResponse>) => {
            state.SyncingContacts = action.payload
        },

        groupContactsDeletion: (state: AppState, action: PayloadAction<DeletedGroupContactData[]>) => {
            state.GroupContactsDeletionData = action.payload
        },

        updateTypingData: (state: AppState, action: PayloadAction<TypingDataModel>) => {
            const data = action.payload as TypingDataModel;
            state.TypingData = data.Action === TypeAction.START_TYPING ? [...state.TypingData.filter(x => x.UserID !== data.UserID), data] : state.TypingData.filter(x => x.ReceiverPhoneNumber !== data.ReceiverPhoneNumber || x.UserID !== data.UserID)
        },

        updateMassContactsDeletionInProgess: (state, action) => {
            state.IsMassContactsDeletionInProgress = action.payload
        },

        handleMassContactsActionEvent: (state, action) => {
            state.HandleMassContactsActionEvent = action.payload
        },

        updateMassGroupsDeletionInProgess: (state, action) => {
            state.IsMassGroupsDeletionInProgress = action.payload
        },

        handleMassGroupsActionEvent: (state, action) => {
            state.HandleMassGroupsActionEvent = action.payload
        },

        loadingSecureFiles: (state, action) => {
            state.LoadingSecureFiles = action.payload
        },

        updateSecureFiles: (state, action) => {
            state.SecureFiles = action.payload;
            state.LoadingSecureFiles = false;
        },

        loadingSecureFilesLinks: (state, action) => {
            state.LoadingSecureFileLinks = action.payload
        },

        updateSecureFilesLinks: (state, action) => {
            state.SecureFileLinks = action.payload;
            state.LoadingSecureFileLinks = false;
        },
        handlePushGroupChatName: (state, action) => {
            let { Conversations } = state;
            let con = Conversations.find(y => y.GroupChat?.ID === action.payload.id)
            if (con) {
                con.GroupChat.Name = action.payload.name;
            }
            state.Conversations = Conversations
        },
        //toaster functions
        setToasts: (state, action) => {
            state.Toasts = [
                {
                    ID: action.payload.id,
                    Text: action.payload.text,
                    Timeout: action.payload.timeout,
                    Type: action.payload.type,
                    Hide: true,
                },
                ...state.Toasts
            ]
        },
        showToast: (state, action) => {
            let x = state.Toasts.find((x) => x.ID === action.payload.id)
            if (x) {
                x.Hide = false
            }

        },
        hideToast: (state, action) => {
            let x = state.Toasts.find((x) => x.ID === action.payload.id)
            if (x) {
                x.Hide = true
            }
        },
        clearToast: (state, action) => {
            state.Toasts = state.Toasts.filter((x) => x.ID !== action.payload.id)
        },
        LoadMsgLogsExports: (state, action) => {
            state.ExportMessageLogs = action.payload
        },
        pushMessageLogsDownloadReport: (state, action) => {
            state.IsMessageLogDownloadReportMsg = action.payload
        },
        HandleWebNotication: (state, action) => {
            state.WebNotificationText = action.payload
        },

        //channels
        updateChannelHistory: (state, action) => {
            if (action.payload.updateLatest && !!state.ChannelHistory[action.payload.history[0].ConversationID]?.length) {
                state.ChannelHistory = pushChannelHistory(state.ChannelHistory, action.payload)
            }
            else {

                if (((action.payload?.updateLatest && localStorage.getItem("IsLiveChat") !== "false") || !action.payload?.updateLatest)) {
                    state.ChannelHistory = {
                        ...state.ChannelHistory,
                        [action.payload.channelConversationId]: action.payload.history.sort((current, previous) => new Date(current.CreatedAt).getTime() - new Date(previous.CreatedAt).getTime()),
                    }
                }
            }
        },

        updateChannelConversationLatest: (state, action) => {
            let con = state.ChannelConversations.find(x => x.ID === action.payload.channelConversationId)
            return con ? {
                ...state,
                ChannelConversations: [{ ...con, Message: action.payload.latest.Message, MessageType: action.payload.latest.MessageType, MessageDateTime: action.payload.latest.DeliverAt },
                ...state.ChannelConversations.filter(x => x.ID !== action.payload.channelConversationId),
                ],
            } : { ...state };
        },

        updateChannels: (state, action) => {
            state.Channels = action.payload?.filter(x => x.Status !== 4)?.filter(x => state.ActiveChannelsTypes.includes(x.ChannelType))
            state.LoadingChannels = false
        },

        LoadingChannels: (state, action) => {
            state.LoadingChannels = action.payload
        },

        updateChannelConversations: (state, action) => {
            let payload = [];
            let WebNotificationText = {};
            if (!action.payload.length) {
                payload = []
            }
            else {
                if (action.payload.length > 1) {
                    payload = action.payload
                }
                else {
                    let convdata = state.ChannelConversations
                    let con = convdata.find(x => x.ID === action.payload[0].ID)
                    if (con) {
                        if (action.payload[0]?.MessageID !== con?.MessageID && !action.payload[0].IsOutbound) {
                            WebNotificationText = action.payload[0]
                        }
                        convdata = [{ ...con, Name: action.payload[0].Name, OnlineStatus: action.payload[0].OnlineStatus, UnReadCount: action.payload[0].UnReadCount, Status: action.payload[0].Status, Message: action.payload[0].Message, MessageDateTime: action.payload[0].MessageDateTime, MessageType: action.payload[0].MessageType, MessageStatus: action.payload[0].MessageStatus, Ticket: action.payload[0].Ticket, UserTyping: action.payload[0].UserTyping, BusinessTyping: action.payload[0].BusinessTyping, TypingBy: action.payload[0].TypingBy, MessageID: action.payload[0]?.MessageID, IsOutbound: action.payload[0].IsOutbound }, ...state.ChannelConversations.filter(x => x.ID !== action.payload[0].ID)]
                        payload = convdata

                    }
                    else {
                        payload = [...convdata, ...action.payload]
                        if (action.payload[0].Status !== ChannelConversationStatus.LIVECHAT_USER_CREATED) {
                            WebNotificationText = action.payload[0]
                        }
                    }
                }
            }

            let finalData = uniqBy([...payload], 'ID');
            return {
                ...state,
                ChannelConversations: finalData,
                LoadingConnect: false,
                WebNotificationText: WebNotificationText
            }

        },

        updateLoadingConnectConversations: (state, action) => {
            state.LoadingConnect = action.payload
        },

        updateChannelTemplates: (state, action) => {
            return {
                ...state,
                ChannelTemplates: action.payload,
            }
        },

        updateFaceboolTemplates: (state, action) => {
            return {
                ...state,
                FacebookTemplates: action.payload,
            }
        },

        updateChannelUsers: (state: AppState, action: PayloadAction<ChannelUserModel[]>) => {
            state.ChannelUsers = action.payload;
        },

        updateActiveChannelsTypes: (state: AppState, action: PayloadAction<number[]>) => {
            state.ActiveChannelsTypes = action.payload;
        },

        updateAllChannelUsers: (state: AppState, action: PayloadAction<ChannelUserModel[]>) => {
            state.ChannelAllUsers = action.payload;
        },

        updateAllChannelOrganizations: (state: AppState, action: PayloadAction<ChannelOrganizationModel[]>) => {
            state.ChannelAllOrganizations = action.payload;
        },

        updateChannelContacts: (state: AppState, action: PayloadAction<ChannelContactModel[]>) => {
            state.ChannelContacts = action.payload;
        },

        updateWordPressDetails: (state: AppState, action) => {
            state.IsWordPress = action.payload.wordpress;
            state.WordPressDomain = action.payload.wordpressDomain
        },

        updateIsShopifyDetails: (state: AppState, action) => {
            state.Shopify = action.payload.shopify;
            state.Shopify_Shop = action.payload.shop
            state.Shopify_Access_Token = action.payload.access_token
        },

        updateTutorials: (state: AppState, action) => {
            state.Tutorials = action.payload
        },

        updateOnBoardingDetails: (state: AppState, action) => {
            state.OnboardingDetails = action.payload
        },

        updatePlanDetails: (state: AppState, action) => {
            state.PlanDetails = action.payload
        },

        updateTrainChatHistoryStatus: (state: AppState, action: PayloadAction<string>) => {
            state.TrainChatHistoryStatus = action.payload;
        },

        updateAiTrainedFileStatus: (state: AppState, action: PayloadAction<any>) => {
            state.AiTrainedFiles = [...state.AiTrainedFiles.filter(x => x.file_name !== action.payload.file_name), action.payload]
        },

        updateAiTrainedUrlStatus: (state: AppState, action: PayloadAction<any>) => {
            action.payload.forEach(newItem => {
                state.AiTrainedUrls = [
                    ...state.AiTrainedUrls.filter(x => x.url !== newItem.url),
                    newItem
                ];
            });
        },

        updateAiTrainedFileList: (state: AppState, action: PayloadAction<[]>) => {
            state.AiTrainedFiles = action.payload;
        },

        updateAiTrainedUrlList: (state: AppState, action: PayloadAction<[]>) => {
            state.AiTrainedUrls = action.payload;
        },

        updateAiFeaturesOrgLevel: (state: AppState, action: PayloadAction<boolean>) => {
            state.AiFeaturesOrgLevel = action.payload;
        },

        updateAiCreditsCount: (state: AppState, action: PayloadAction<number>) => {
            state.AiCreditsCount = action.payload;
        },

        updateChannelPlans: (state: AppState, action: PayloadAction<[]>) => {
            state.channelPlans = action.payload;
        },
    },
})

export const {
    updateUserRoleId,
    updateConversations,
    updateContactTypes,
    updateContactTypesInConversation,
    updateContacts,
    updateContactsInConversation,
    updateConversation,
    updateFeatures,
    updateGlobalAutomationRules,
    updateLoadingConversations,
    updateGroups,
    updatePhoneNumber,
    updatePlanHistory,
    updatePlanMetrics,
    updatePlans,
    updatePrivateAutomationRules,
    // updateProfile,
    updateProviders,
    updateReports,
    updateRoles,
    updateSettings,
    updateUnsubscribedContacts,
    updateUserAlertNotifaction,
    updateHistory,
    updateKeywords,
    updateLoadingAlertNotification,
    updateLoadingContacts,
    updateLoadingFeatures,
    updateLoadingGlobalAutomationRules,
    updateLoadingGroups,
    updateLoadingKeywords,
    updateLoadingOrganizations,
    updateLoadingPhoneNumbers,
    updateLoadingPlanes,
    updateLoadingPrivateAutomationRules,
    // updateLoadingProfile,
    updateLoadingProviders,
    updateMobileDevice,
    updateOrgName,
    updateOrganizations,
    updatePhoenNumbers,
    updateTemplates,
    updateLoadingTemplates,
    updateMenuFlag,
    updateUsers,
    updateLoadingUsers,
    clearHistory,
    loadingReports,
    pushContacts,
    pushConversations,
    backHandler,
    headerCount,
    headerTitle,
    refreshHandler,
    setShowingAlert,
    updateAlertNotification,
    updateBlockedContacts,
    updateContact,
    loadingKafkaOrg,
    setKafka,
    updateKafka,
    cleanHistory,
    pushGroups,
    pushGroupChat,
    syncingContacts,
    groupContactsDeletion,
    updateTypingData,
    updateMassContactsDeletionInProgess,
    updateMassGroupsDeletionInProgess,
    loadingSecureFiles,
    updateSecureFiles,
    loadingSecureFilesLinks,
    updateSecureFilesLinks,
    updateNewMessageRecived,
    pushMessageLogsDownloadReport,
    handlePushGroupChatName,
    clearToast,
    hideToast,
    setToasts,
    showToast,
    LoadMsgLogsExports,
    HandleWebNotication,


    updateChannelHistory,
    updateChannelConversationLatest,
    updateChannels,
    LoadingChannels,
    updateChannelConversations,
    updateLoadingConnectConversations,
    updateChannelTemplates,
    updateFaceboolTemplates,
    updateChannelUsers,
    updateActiveChannelsTypes,
    updateAllChannelUsers,
    updateAllChannelOrganizations,
    updateChannelContacts,
    updateWordPressDetails,
    updateIsShopifyDetails,
    updateTutorials,
    updateOnBoardingDetails,
    updatePlanDetails,
    updateTrainChatHistoryStatus,
    updateAiTrainedFileList,
    updateAiTrainedUrlList,
    updateAiTrainedFileStatus,
    updateAiTrainedUrlStatus,
    updateAiFeaturesOrgLevel,
    updateAiCreditsCount,
    updateChannelPlans
} = counterSlice.actions

export default counterSlice.reducer